import { z } from 'zod';

/**
 * Les rôles, du haut vers le terrain : chacun crée et voit ce qui est sous
 * lui. `responsable_backoffice` (réunion Cyrille 16/09/2026) est le
 * back-office salarié des grands réseaux (LCT, Exosphère) : il relance les
 * vendeurs, extrait les données de facturation et crée les profils, sans
 * avoir personne rattaché à lui. Il voit tout son réseau comme un
 * responsable partenaire et crée tout ce qui est sous lui — managers
 * compris (« le responsable partenaire délègue tout en fait », Cyrille
 * 16/09, après démonstration aux réseaux) — mais ne touche pas à la
 * configuration et ne crée ni RP ni back-office : il ne se clone pas et ne
 * se donne pas de supérieur. Facultatif ; créé par le responsable partenaire.
 */
export const UserRole = {
  admin: 'admin',
  responsable_partenaire: 'responsable_partenaire',
  responsable_backoffice: 'responsable_backoffice',
  manager: 'manager',
  superviseur: 'superviseur',
  vendeur: 'vendeur',
} as const;

export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserRoleSchema = z.nativeEnum(UserRole);

/** Du haut de la hiérarchie vers le terrain — l'ordre d'affichage partout. */
export const USER_ROLE_ORDER: readonly UserRole[] = [
  'admin',
  'responsable_partenaire',
  'responsable_backoffice',
  'manager',
  'superviseur',
  'vendeur',
];

/** Rang dans l'échelle : 0 = admin … 5 = vendeur. Dérivé de l'ordre, jamais recopié. */
function rank(role: UserRole): number {
  return USER_ROLE_ORDER.indexOf(role);
}

/**
 * `actor` peut-il créer, inviter ou promouvoir quelqu'un au rôle `target` ?
 * Une seule règle, lue par l'API (`HierarchyService.validateCreate`) et par
 * les écrans (liste des rôles proposés) — sinon le formulaire propose ce que
 * l'API refuse, et le refus n'arrive qu'après la saisie.
 *  - admin : tout, y compris un autre admin ;
 *  - tous les autres : strictement en dessous d'eux dans `USER_ROLE_ORDER`.
 * Aucune exception par rôle : le back-office crée managers, superviseurs et
 * vendeurs parce qu'il est au-dessus d'eux, et ne crée ni RP ni back-office
 * parce qu'il ne l'est pas. Les dérogations tenant (agrégateur qui crée le RP
 * d'une filiale) restent dans l'API : elles dépendent de la donnée.
 *
 * La même règle vaut pour **reprendre la main** sur un compte existant —
 * modifier, archiver, réattribuer mot de passe ou PIN, gérer une invitation :
 * on ne touche qu'à ce qu'on aurait pu créer. Sans ça, un rôle qui voit tout
 * son réseau (RP, back-office) pourrait rétrograder ou archiver son supérieur.
 */
export function canCreateRole(actor: UserRole, target: UserRole): boolean {
  if (actor === 'admin') return true;
  return rank(target) > rank(actor);
}

/**
 * Rôles dont le périmètre est **tout le réseau** (le partner, et ses filiales
 * pour un agrégateur) : admin, responsable partenaire, responsable
 * back-office. À distinguer de « administre le réseau » — la configuration,
 * qui reste admin + RP.
 */
export function seesWholeNetwork(role: UserRole): boolean {
  return role === 'admin' || role === 'responsable_partenaire' || role === 'responsable_backoffice';
}

export const USER_ROLE_LABEL_FR: Record<UserRole, string> = {
  admin: 'Administrateur',
  responsable_partenaire: 'Responsable partenaire',
  responsable_backoffice: 'Responsable back-office',
  manager: 'Manager',
  superviseur: 'Superviseur',
  vendeur: 'Vendeur',
};
